import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LeftAccentCard from '../components/LeftAccentCard';
import apiService from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { hasPendingWithdrawalRequest as hasPendingWithdrawalRequestInList } from './walletStatus';
import { formatJstDateTime } from '../utils/datetime';
import {
  WalletBalance,
  WalletTransactionItem,
  WithdrawalBlockReason,
  WithdrawalHistoryItem,
  WithdrawalSimulation,
} from '../types';
import '../styles/WalletPage.css';

// 引き出し履歴の 1 行（親 + 展開可能な企業別 allocation）
const WithdrawalHistoryRow: React.FC<{
  request: WithdrawalHistoryItem;
  formatCurrency: (n: number) => string;
  formatDate: (s: string) => string;
  getParentStatusBadge: (s: string) => string;
}> = ({ request, formatCurrency, formatDate, getParentStatusBadge }) => {
  const [expanded, setExpanded] = useState(false);
  const hasAllocations = request.allocations && request.allocations.length > 0;
  const hasMultiple = request.allocation_count >= 2;

  return (
    <div className="transaction-item">
      <div className="transaction-header">
        <span className="type-badge">{getParentStatusBadge(request.parent_status)}</span>
        <span className="date">{request.created_at ? formatDate(request.created_at) : '-'}</span>
      </div>
      <div className="transaction-details">
        <div className="transaction-row">
          <span className="label">申請金額:</span>
          <span className="value">{formatCurrency(request.total_requested_amount)}</span>
        </div>
        {request.total_transfer_amount !== null && (
          <div className="transaction-row">
            <span className="label">実振込額:</span>
            <span className="value">{formatCurrency(request.total_transfer_amount)}</span>
          </div>
        )}
        {/* 振込手数料は契約上つねに企業負担（SOKYU_FEE_SHARE_KBN_FIXED="2"）で
            ワーカーの受取額から差し引かれないため、手数料はワーカーに表示しない */}
        <div className="transaction-row">
          <span className="label">振込回数:</span>
          <span className="value">{request.allocation_count}回</span>
        </div>

        {hasAllocations && (
          <>
            <Button
              size="small"
              onClick={() => setExpanded((v) => !v)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ mt: 0.5, color: '#13c3a8', textTransform: 'none', fontSize: '12px' }}
            >
              {expanded ? '内訳を閉じる' : `店舗別内訳${hasMultiple ? `（${request.allocation_count}店舗）` : ''}`}
            </Button>
            <Collapse in={expanded}>
              <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid #e0f7f4' }}>
                {request.allocations.map((a) => (
                  <Box key={a.allocation_id} sx={{ py: 0.5, borderBottom: '1px dashed #eee' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.store_name ?? a.company_name}</Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          px: 0.75,
                          borderRadius: 0.5,
                          bgcolor:
                            a.status === 'accepted' ? '#e8f5e9'
                            : a.status === 'failed' ? '#ffebee'
                            : '#fff3e0',
                          color:
                            a.status === 'accepted' ? '#2e7d32'
                            : a.status === 'failed' ? '#c62828'
                            : '#e65100',
                        }}
                      >
                        {a.status === 'accepted' ? '成功' : a.status === 'failed' ? '失敗' : a.status}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      申請 {formatCurrency(a.requested_amount)}
                    </Typography>
                    {a.transfer_scheduled_date && (
                      <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                        振込予定日: {a.transfer_scheduled_date}
                      </Typography>
                    )}
                    {/* 即給の生エラー文言は企業担当者向けのためワーカーには表示せず、
                        バックエンドで変換したワーカー向け文言（error_message）を出す。
                        未変換時のみ error_summary にフォールバック。 */}
                    {(a.error_message || a.error_summary) && (
                      <Typography variant="caption" sx={{ color: '#c62828', display: 'block' }}>
                        {a.error_message || a.error_summary}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * 引き出しできない理由ごとの文言。
 *
 * 以前は理由に依らず「今すぐ引き出せる額がありません。」を出していたため、残高が
 * 最低額（1,000円）を超えているのにボタンが押せないケース（店舗の事業所番号未入力・
 * 即給の店舗登録未完了など）で文言が事実と食い違い、原因も分からなかった。
 * 理由コードは backend の withdrawal_block_reason（INELIGIBLE_REASON_*）に対応する。
 */
const withdrawalBlockMessage = (reason?: WithdrawalBlockReason | null): string => {
  switch (reason) {
    case 'store_not_usable':
      return 'この店舗は即払い（前払い）の設定が完了していないため、引き出しできません。報酬は次回給与でお受け取りになります。店舗にお問い合わせください。';
    case 'not_store_registered':
      return '即払い（前払い）のご利用登録が完了していないため、引き出しできません。しばらくしてから再度お試しください（解消しない場合は店舗にお問い合わせください）。';
    case 'store_opt_out':
      return 'この店舗では即払いの引き出しをご利用いただけません。報酬は次回給与でお受け取りになります。';
    case 'user_opt_out':
      return '即払いの引き出しをご利用いただけない設定になっています。報酬は次回給与でお受け取りになります。';
    case 'company_sokyu_disabled':
      return 'この企業では即払いの引き出しをご利用いただけません。報酬は次回給与でお受け取りになります。';
    case 'sokyu_sync_frozen':
      return '残高の確認中のため、一時的に引き出しできません。しばらくしてから再度お試しください。';
    case 'no_wallet':
      return '引き出し可能な残高がまだありません。勤務の承認後にお引き出しいただけます。';
    case 'minimum_not_met':
    case 'no_balance':
    default:
      return '今すぐ引き出せる額がありません。引き出しには店舗ごとに一定額以上の残高が必要です（不足分は次回給与でお受け取りになります）。';
  }
};

const WalletPage: React.FC = () => {
  const { userId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [storeUserId, setStoreUserId] = useState<string>('');
  const [walletInfo, setWalletInfo] = useState<WalletBalance | null>(null);
  const [transactions, setTransactions] = useState<WalletTransactionItem[]>([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState<WithdrawalHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showCompanyBreakdown, setShowCompanyBreakdown] = useState<boolean>(false);

  const [isWithdrawing, setIsWithdrawing] = useState<boolean>(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState<boolean>(false);
  const [withdrawalError, setWithdrawalError] = useState<string>('');
  const [hasBankAccount, setHasBankAccount] = useState<boolean | null>(null);
  const [showBankAccountRequiredModal, setShowBankAccountRequiredModal] = useState<boolean>(false);

  // 即給非利用（管理画面登録）ワーカーは残高・履歴のみ表示し、引き出し・口座UIを出さない。
  // null = 判定未確定。false 初期値だと getUser 解決前に口座登録誘導 alert が
  // 発火し得る（opt-out ワーカーは残高あり・口座未登録が通常状態）ため区別する。
  const [sokyuOptOut, setSokyuOptOut] = useState<boolean | null>(null);
  useEffect(() => {
    if (!userId) return;
    apiService
      .getUser(userId)
      .then((res) => {
        setSokyuOptOut(res?.success && res.data ? Boolean(res.data.sokyu_opt_out) : false);
      })
      .catch(() => setSokyuOptOut(false));
  }, [userId]);

  // 全額引き出しのプレビュー（モーダル表示時に取得）
  const [simulation, setSimulation] = useState<WithdrawalSimulation | null>(null);
  const [simulating, setSimulating] = useState<boolean>(false);

  const currentYearMonthInit = (() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  })();
  const [withdrawalYearMonth, setWithdrawalYearMonth] = useState<string>(currentYearMonthInit);
  const [transactionYearMonth, setTransactionYearMonth] = useState<string>(currentYearMonthInit);

  const locationState = location.state as { storeUserId?: string } | null;

  const resolveStoreUserId = useCallback(async () => {
    const stateStoreUserId = locationState?.storeUserId;
    if (stateStoreUserId) {
      localStorage.setItem('store_user_id', stateStoreUserId);
      setStoreUserId(stateStoreUserId);
      return;
    }

    const savedStoreUserId = localStorage.getItem('store_user_id');
    if (savedStoreUserId) {
      setStoreUserId(savedStoreUserId);
      return;
    }

    if (!userId) {
      return;
    }

    const storeUserResponse = await apiService.getStoreUser(userId);
    if (storeUserResponse.success && storeUserResponse.data && storeUserResponse.data.length > 0) {
      const primaryStoreUserId = storeUserResponse.data[0].id;
      localStorage.setItem('store_user_id', primaryStoreUserId);
      setStoreUserId(primaryStoreUserId);
    } else {
      setError('店舗情報が見つからないため、ウォレットを表示できません。');
    }
  }, [locationState?.storeUserId, userId]);

  const fetchWalletData = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError('');
      const balanceResponse = await apiService.getWalletBalance(userId);
      if (balanceResponse.success && balanceResponse.data) {
        setWalletInfo(balanceResponse.data);
      } else {
        setWalletInfo(null);
        if (!balanceResponse.success && balanceResponse.error) {
          setError(balanceResponse.error);
        }
      }
    } catch (err: any) {
      setError(err.message || 'ウォレット情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchWithdrawalRequests = useCallback(
    async (yearMonth: string) => {
      if (!storeUserId) return;
      try {
        const withdrawalResponse = await apiService.getWalletWithdrawalRequests(
          storeUserId,
          200,
          0,
          yearMonth,
        );
        if (withdrawalResponse.success && withdrawalResponse.data) {
          setWithdrawalRequests(withdrawalResponse.data.requests || []);
        } else {
          setWithdrawalRequests([]);
        }
      } catch {
        setWithdrawalRequests([]);
      }
    },
    [storeUserId],
  );

  const fetchTransactions = useCallback(
    async (yearMonth: string) => {
      if (!userId) return;
      try {
        const [yearStr, monthStr] = yearMonth.split('-');
        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10);
        const dateFrom = `${yearStr}-${monthStr}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const dateTo = `${yearStr}-${monthStr}-${String(lastDay).padStart(2, '0')}`;

        const historyResponse = await apiService.getWalletTransactions(userId, 200, 0, {
          dateFrom,
          dateTo,
        });
        if (historyResponse.success && historyResponse.data) {
          setTransactions(historyResponse.data.transactions || []);
        } else {
          setTransactions([]);
        }
      } catch {
        setTransactions([]);
      }
    },
    [userId],
  );

  useEffect(() => {
    resolveStoreUserId();
  }, [resolveStoreUserId]);

  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  useEffect(() => {
    fetchWithdrawalRequests(withdrawalYearMonth);
  }, [withdrawalYearMonth, fetchWithdrawalRequests]);

  useEffect(() => {
    fetchTransactions(transactionYearMonth);
  }, [transactionYearMonth, fetchTransactions]);

  useEffect(() => {
    const checkBankAccount = async () => {
      if (!userId) return;
      try {
        const response = await apiService.getBankAccount(userId);
        if (response.success && response.data) {
          setHasBankAccount(response.data.has_bank_account);
        }
      } catch {
        // 口座情報取得に失敗しても致命的ではない
      }
    };
    checkBankAccount();
  }, [userId]);

  // 口座未登録の告知はブラウザ標準の alert() を使わない。
  // LINE内ブラウザの alert はタイトルにサーバのドメイン名が出てしまうため、
  // アプリ内モーダル（タイトル「口座未登録」）で案内し、ボタンで口座登録画面へ送る。
  useEffect(() => {
    if (walletInfo && walletInfo.total_balance > 0 && hasBankAccount === false && sokyuOptOut === false) {
      setShowBankAccountRequiredModal(true);
    }
  }, [walletInfo, hasBankAccount, sokyuOptOut]);

  // モーダルを開いた時点で1回だけ「全額引き出しプレビュー」を取得する
  useEffect(() => {
    if (!showWithdrawalModal || !userId) {
      setSimulation(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setSimulating(true);
        setWithdrawalError('');
        const res = await apiService.simulateWithdrawal(userId);
        if (cancelled) return;
        if (res.success && res.data) {
          setSimulation(res.data);
        } else {
          setSimulation(null);
          setWithdrawalError(res.error || 'シミュレーションに失敗しました');
        }
      } catch (e: any) {
        if (cancelled) return;
        setSimulation(null);
        setWithdrawalError(e.message || 'シミュレーションに失敗しました');
      } finally {
        if (!cancelled) setSimulating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showWithdrawalModal, userId]);

  const handleWithdrawalClick = () => {
    if (hasBankAccount === false) {
      navigate('/bank-account');
      return;
    }

    if (hasPendingWithdrawalRequest) {
      setError('処理中の引き出し申請があります。完了後に再度お試しください。');
      return;
    }

    setWithdrawalError('');
    setSimulation(null);
    setShowWithdrawalModal(true);
  };

  const handleCreateWithdrawal = async () => {
    setWithdrawalError('');

    if (!userId) {
      setWithdrawalError('ユーザー情報が取得できませんでした');
      return;
    }

    if (hasPendingWithdrawalRequest) {
      setWithdrawalError('処理中の引き出し申請があります。完了後に再度お試しください。');
      return;
    }

    if (!simulation || simulation.eligible_count === 0) {
      setWithdrawalError('引き出し可能な残高がありません');
      return;
    }

    try {
      setIsWithdrawing(true);
      setError('');
      const response = await apiService.executeWithdrawal(userId);

      if (response.success && response.data) {
        const result = response.data;
        if (result.parent_status === 'success') {
          setSuccessMessage('引き出し申請を作成しました');
        } else if (result.parent_status === 'partial_success') {
          setSuccessMessage('引き出し申請の一部が成功しました。履歴から詳細をご確認ください。');
        } else {
          setError('引き出し申請に失敗しました。時間を空けて再度お試しください。');
        }
        setShowWithdrawalModal(false);
        setSimulation(null);
        await fetchWalletData();
        await fetchWithdrawalRequests(withdrawalYearMonth);
      } else {
        setWithdrawalError(response.error || '引き出し申請に失敗しました');
      }
    } catch (err: any) {
      setWithdrawalError(err.message || '引き出し申請中にエラーが発生しました');
    } finally {
      setIsWithdrawing(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => formatJstDateTime(dateString);

  // バックエンドが実際に生成する取引種別のみを載せる。
  // earning / deduction / adjustment は生成経路が存在しない死にラベルだったため削除した
  // （manual_adjust は保守運用で手動投入され得るので残す）。
  const getTransactionTypeBadge = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      wage_earning: '就労承認',
      transfer: '振込',
      reversal: '取消',
      manual_adjust: '手動調整',
      sokyu_sync: '即給同期',
    };
    return typeMap[type] || type;
  };

  const getParentStatusBadge = (parentStatus: string): string => {
    const statusMap: { [key: string]: string } = {
      pending: '処理中',
      success: '成功',
      partial_success: '一部成功',
      failed: '失敗',
    };
    return statusMap[parentStatus] || parentStatus;
  };

  const hasPendingWithdrawalRequest = hasPendingWithdrawalRequestInList(withdrawalRequests);

  if (loading && !walletInfo) {
    return (
      <div className="wallet-page">
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 1.5, pb: 0.5, minHeight: 48 }}>
          <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: '#3c3c3c' }}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ flex: 1, fontWeight: 700, fontSize: '16px', color: '#3c3c3c', pl: 0.5, textAlign: 'left' }}>
            ウォレット管理
          </Typography>
        </Box>
        <div className="wallet-container">
          <p className="loading">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-page">
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 1.5, pb: 0.5, minHeight: 48 }}>
        <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: '#3c3c3c' }}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ flex: 1, fontWeight: 700, fontSize: '16px', color: '#3c3c3c', pl: 0.5, textAlign: 'left' }}>
          ウォレット管理
        </Typography>
      </Box>
      <div className="wallet-container">
        {error && <div className="alert alert-error">{error}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        {/* 合算残高 + 企業別内訳 */}
        {walletInfo && (
          <LeftAccentCard accentColor="#13c3a8" borderColor="#b2ede7" padding="16px 20px" sx={{ mb: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <Box sx={{ position: 'absolute', top: -8, right: -8 }}>
                <IconButton
                  size="small"
                  onClick={fetchWalletData}
                  disabled={loading}
                  title="残高を更新"
                  sx={{ color: '#13c3a8' }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
              {/* 稼いでいる額（未受取）を主見出しにし、その内訳として
                  「今すぐ即給で引き出せる額」＋「次回給与でお受け取り」を並べる。
                  合計は勤怠ベース残高（稼いだ未受取）に一致するクリーンな内訳。 */}
              <Typography variant="caption" sx={{ color: '#888' }}>稼いでいる額（未受取）</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '28px', color: '#1a1a1a', lineHeight: 1.3 }}>
                {formatCurrency(walletInfo.attendance_balance ?? 0)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 1.5 }}>
                承認済みで、まだお受け取りになっていない報酬の合計です。
              </Typography>

              {/* 内訳: 今すぐ即給で引き出せる / 次回給与でお受け取り */}
              <Box sx={{ display: 'flex', gap: 1.25, mb: 1.5 }}>
                {sokyuOptOut === false && (
                  <Box sx={{ flex: 1, p: 1.25, borderRadius: 1.5, bgcolor: '#f0f9f7', border: '1px solid #b2ede7' }}>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.25 }}>
                      今すぐ即給で引き出せる
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '18px', color: walletInfo.withdrawable_total > 0 ? '#13c3a8' : '#aaa' }}>
                      {formatCurrency(walletInfo.withdrawable_total)}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ flex: 1, p: 1.25, borderRadius: 1.5, bgcolor: '#fff8f0', border: '1px solid #ffe0b2' }}>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.25 }}>
                    次回給与でお受け取り
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#e65100' }}>
                    {formatCurrency(walletInfo.total_pending_next_payroll)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>累計収益</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(walletInfo.total_earned)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>累計振込</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(walletInfo.total_transferred)}</Typography>
                </Box>
              </Box>

              {/* 店舗別内訳（複数店舗時のみ折りたたみで表示）。
                  store_id=null の行は企業フォールバック（未分割ウォレット）で店舗ではないため
                  「店舗別」の一覧・カウントからは除外する。 */}
              {(() => {
                const storeItems = walletInfo.companies.filter((c) => c.store_id);
                if (storeItems.length <= 1) return null;
                return (
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      size="small"
                      endIcon={showCompanyBreakdown ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      onClick={() => setShowCompanyBreakdown((v) => !v)}
                      sx={{ color: '#13c3a8', fontSize: '12px', textTransform: 'none' }}
                    >
                      店舗別内訳（{storeItems.length}店舗）
                    </Button>
                    <Collapse in={showCompanyBreakdown}>
                      <Box sx={{ mt: 1, pl: 1 }}>
                        {storeItems.map((c) => (
                          <Box
                            key={c.store_id}
                            sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderTop: '1px solid #e0f7f4' }}
                          >
                            <Typography variant="body2">{c.store_name ?? c.company_name}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(c.balance)}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })()}
            </Box>

            {hasBankAccount === true && sokyuOptOut === false && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, pt: 1, borderTop: '1px solid #e0f7f4' }}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<AccountBalanceIcon fontSize="small" />}
                  onClick={() => navigate('/bank-account')}
                  sx={{ color: '#13c3a8', fontSize: '12px' }}
                >
                  口座変更
                </Button>
              </Box>
            )}
          </LeftAccentCard>
        )}

        {/* 次回給与でお受け取り。
            store_id=null の企業フォールバック行は「店舗の金額詳細」ではないので除外する
            （1企業1店舗ワーカーは初回勤怠承認で null_wallet に store_id が in-place 刻印されるため、
             通常運用ではここに残るのは過去の過渡期の残余のみ）。 */}
        {walletInfo && walletInfo.companies.some((c) => c.store_id && c.pending_next_payroll_amount > 0) && (
          <LeftAccentCard
            accentColor="#ffa726"
            borderColor="#ffe0b2"
            padding="12px 16px"
            sx={{ mb: 2 }}
          >
            <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600 }}>
              💡 次回給与でお受け取り
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
              締日を過ぎた勤務分は、次回給与日に通常の銀行振込でお支払いされます。
            </Typography>
            {walletInfo.companies
              .filter((c) => c.store_id && c.pending_next_payroll_amount > 0)
              .map((c) => (
                <Box
                  key={c.store_id}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.5, borderTop: '1px solid #ffe0b2' }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {c.store_name ?? c.company_name}
                    </Typography>
                    {c.next_salary_pay_day !== null && (
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        給与支払日:
                        {c.next_salary_pay_mw === '1' ? ' 翌月' : c.next_salary_pay_mw === '2' ? ' 翌々月' : ''}
                        {c.next_salary_pay_day === 99 ? ' 月末' : ` ${c.next_salary_pay_day}日`}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#e65100' }}>
                    {formatCurrency(c.pending_next_payroll_amount)}
                  </Typography>
                </Box>
              ))}
          </LeftAccentCard>
        )}

        {/* アクションボタン（即給非利用ワーカーには表示しない） */}
        {sokyuOptOut ? (
          <LeftAccentCard
            accentColor="#bdbdbd"
            borderColor="#e0e0e0"
            padding="12px 16px"
            sx={{ mb: 2 }}
          >
            <Typography variant="caption" sx={{ color: '#666' }}>
              報酬は給与日に通常の銀行振込でお支払いされます（即払いの引き出しは利用できません）。
            </Typography>
          </LeftAccentCard>
        ) : sokyuOptOut === false ? (
          <div className="action-buttons">
            <button
              className="btn btn-withdrawal"
              onClick={handleWithdrawalClick}
              disabled={
                hasBankAccount === false
                  ? false
                  : !walletInfo || (walletInfo.withdrawable_company_count ?? 0) <= 0 || hasPendingWithdrawalRequest
              }
            >
              {hasBankAccount === false ? '口座登録' : '引き出し申請'}
            </button>

            {/* 課題10: 引き出しボタンが非活性なとき、理由が画面に出ないと混乱するため明示する */}
            {hasBankAccount === true && hasPendingWithdrawalRequest && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#888' }}>
                引き出し申請を処理中です。完了までしばらくお待ちください。
              </Typography>
            )}
            {hasBankAccount === true
              && !hasPendingWithdrawalRequest
              && walletInfo
              && (walletInfo.withdrawable_company_count ?? 0) <= 0
              && walletInfo.total_balance > 0 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#888' }}>
                {withdrawalBlockMessage(walletInfo.withdrawal_block_reason)}
              </Typography>
            )}
          </div>
        ) : null}

        {/* 引き出し履歴 */}
        <div className="transactions-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h2 style={{ margin: 0 }}>引き出し履歴</h2>
            <input
              type="month"
              value={withdrawalYearMonth}
              max={currentYearMonthInit}
              onChange={(e) => e.target.value && setWithdrawalYearMonth(e.target.value)}
              style={{ fontSize: '14px', border: '1px solid #ccc', borderRadius: '6px', padding: '4px 8px', color: '#333', background: '#fafafa' }}
            />
          </div>
          {withdrawalRequests.length > 0 ? (
            <div className="transactions-list">
              {withdrawalRequests.map((request) => (
                <WithdrawalHistoryRow
                  key={request.id}
                  request={request}
                  formatCurrency={formatCurrency}
                  formatDate={formatDate}
                  getParentStatusBadge={getParentStatusBadge}
                />
              ))}
            </div>
          ) : (
            <p className="no-transactions">引き出し申請はまだありません</p>
          )}
        </div>

        {/* 取引履歴 */}
        <div className="transactions-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h2 style={{ margin: 0 }}>取引履歴</h2>
            <input
              type="month"
              value={transactionYearMonth}
              max={currentYearMonthInit}
              onChange={(e) => e.target.value && setTransactionYearMonth(e.target.value)}
              style={{ fontSize: '14px', border: '1px solid #ccc', borderRadius: '6px', padding: '4px 8px', color: '#333', background: '#fafafa' }}
            />
          </div>
          {transactions.length > 0 ? (
            <div className="transactions-list">
              {transactions.map((t, index) => (
                <div key={`${t.id}-${index}`} className="transaction-item">
                  <div className="transaction-header">
                    <span className="type-badge">{getTransactionTypeBadge(t.transaction_type)}</span>
                    <span className="date">{formatDate(t.created_at)}</span>
                  </div>
                  <div className="transaction-details">
                    {t.description && <p className="description">{t.description}</p>}
                    <div className="transaction-row">
                      <span className="label">金額:</span>
                      {/* 符号は種別ではなく金額そのものから決める。
                          振込(-)・承認取消(-)・即給同期(±)は金額側に符号が入っており、
                          種別で判定すると取消や減額同期が「+」と表示されてしまう。 */}
                      <span
                        className={`amount ${t.amount < 0 ? 'debit' : 'credit'}`}
                      >
                        {t.amount < 0 ? '-' : '+'}
                        {formatCurrency(Math.abs(t.amount))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-transactions">取引履歴がありません</p>
          )}
        </div>

        {/* 口座未登録の案内モーダル（残高あり・口座未登録のとき） */}
        {showBankAccountRequiredModal && (
          <div className="modal-overlay">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>口座未登録</h2>
              <div className="modal-body">
                <Typography variant="body2" sx={{ color: '#3c3c3c' }}>
                  残高があります。引き出しには口座情報の登録が必要です。
                </Typography>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-confirm"
                  onClick={() => {
                    setShowBankAccountRequiredModal(false);
                    navigate('/bank-account');
                  }}
                >
                  口座を登録する
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 全額引き出し申請モーダル */}
        {showWithdrawalModal && (
          <div className="modal-overlay" onClick={() => setShowWithdrawalModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>引き出し申請（全額）</h2>
              <div className="modal-body">
                {simulating && (
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      引き出し可能額を計算中...
                    </Typography>
                  </Box>
                )}
                {!simulating && simulation && simulation.eligible_count > 0 && (() => {
                  // 「店舗の金額詳細」なので store_id=null の企業フォールバック行は表示しない。
                  // カウントもフィルタ後の長さを使う（backend の eligible_count は企業行も含むため）。
                  const eligibleStores = simulation.items.filter((it) => it.store_id && it.eligible);
                  const carryOverStores = simulation.items.filter((it) => it.store_id && !it.eligible);
                  return (
                  <Box sx={{ p: 1.5, bgcolor: '#f0f9f7', borderRadius: 1, border: '1px solid #b2ede7' }}>
                    <Typography variant="caption" sx={{ color: '#13c3a8', fontWeight: 600 }}>
                      引き出し可能額（全額）
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '24px', color: '#1a1a1a', mt: 0.5 }}>
                      {formatCurrency(simulation.total_transfer_amount)}
                    </Typography>
                    {/* 手数料は企業負担固定でワーカーの受取額から差し引かれないため、
                        「手数料を差し引いた金額」の注記は表示しない */}

                    {/* 対象店舗の内訳 */}
                    <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid #b2ede7' }}>
                      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
                        対象店舗（{eligibleStores.length}店舗）
                      </Typography>
                      {eligibleStores.map((it) => (
                        <Box
                          key={it.store_id}
                          sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}
                        >
                          <Typography variant="body2">{it.store_name ?? it.company_name ?? '-'}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(it.balance)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* 最低額未満で持ち越しになる店舗 */}
                    {carryOverStores.length > 0 && (
                      <Box sx={{ mt: 1.5, p: 1, bgcolor: '#fff3e0', borderRadius: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, display: 'block' }}>
                          次回以降に持ち越し（残高 {formatCurrency(simulation.minimum_per_company)} 未満）
                        </Typography>
                        {carryOverStores.map((it) => (
                          <Box
                            key={it.store_id}
                            sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}
                          >
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              {it.store_name ?? it.company_name ?? '-'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              {formatCurrency(it.balance)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {eligibleStores.length >= 2 && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#e3f2fd', borderRadius: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#1565c0' }}>
                          {eligibleStores.length} 店舗に分割して振込されます。着金日は店舗ごとに異なる場合があります。
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  );
                })()}
                {!simulating && simulation && simulation.eligible_count === 0 && (
                  <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffe0b2' }}>
                    <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                      引き出し可能な残高がありません
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                      各店舗の残高が {formatCurrency(simulation.minimum_per_company)} 以上になると引き出し可能になります。
                    </Typography>
                  </Box>
                )}

                {withdrawalError && (
                  <p style={{ color: '#d32f2f', fontSize: '12px', margin: '8px 0 0' }}>{withdrawalError}</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-cancel"
                  onClick={() => setShowWithdrawalModal(false)}
                  disabled={isWithdrawing}
                >
                  キャンセル
                </button>
                <button
                  className="btn btn-confirm"
                  onClick={handleCreateWithdrawal}
                  disabled={
                    isWithdrawing ||
                    simulating ||
                    !simulation ||
                    simulation.eligible_count === 0
                  }
                >
                  {isWithdrawing ? '申請中...' : '全額引き出す'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletPage;
